import { pieceColorVar } from "@/game/pieces";
import type { Section } from "@/lib/sections";
import { useReveal } from "@/lib/useReveal";
import { Tetromino } from "./Tetromino";

interface SectionHeaderProps {
  readonly section: Section;
  readonly kicker?: string;
  /** Set on the dark band, where an ink rule and ink keylines would vanish. */
  readonly onInk?: boolean;
}

/**
 * Every section is stamped with its piece, its index, and a rule that draws
 * itself in like a cleared line.
 */
export function SectionHeader({ section, kicker, onInk = false }: SectionHeaderProps) {
  const ruleRef = useReveal<HTMLDivElement>(0.6);
  const edge = onInk ? "border-field" : "border-ink";

  return (
    <div className="mb-10 sm:mb-12 md:mb-14">
      <div className="flex flex-wrap items-end gap-x-5 gap-y-4">
        <span
          className={`flex h-14 w-14 shrink-0 items-center justify-center border-2 ${edge}`}
          style={{ background: pieceColorVar(section.piece) }}
        >
          <Tetromino type={section.piece} cell={9} title={`${section.piece} piece`} />
        </span>

        <div>
          <span className="meta block opacity-70">
            {section.index} — {section.piece} piece
          </span>
          <h2 className="text-[clamp(2.25rem,6.5vw,4.5rem)]">{section.label}</h2>
        </div>

        {kicker ? (
          <span
            className="meta mt-1 w-full max-w-[34ch] leading-relaxed opacity-70
              sm:mt-0 sm:mb-1.5 sm:ml-auto sm:w-auto sm:max-w-[22ch]"
          >
            {kicker}
          </span>
        ) : null}
      </div>

      <div
        ref={ruleRef}
        className={`rule-clear mt-5 h-1 w-full origin-left ${onInk ? "bg-field" : "bg-ink"}`}
        data-shown="false"
      />
    </div>
  );
}
